import PageShell from '@/pages/shared/PageShell';

export default function AiTaskBreakdown() {
  return (
    <PageShell
      title="AIタスク分解｜資料からWBSとタスクを自動生成｜”おもいむすび”"
      description="企画書やPDFをアップロードするだけで、AIがタスクを分解しWBSを自動生成。担当者候補、期限、依存関係、マイルストーンを提案。人が確認・承認してから反映します。"
      keywords="WBS 自動生成 AI,タスク分解 AI,AI WBS 作成,PDF タスク 自動生成,企画書 タスク 自動生成"
      canonical="https://omoimusubi.asia/features/ai-task-breakdown/"
      h1="資料からWBSとタスクを自動生成するAIタスク分解"
      subtitle="企画書、提案書、WBS、プロジェクト資料などのPDFやファイルをアップロードすると、AIが内容を読み取り、必要な作業をタスクに分解します。"
      relatedLinks={[
        { label: 'AIガントチャート', path: '/features/ai-gantt/' },
        { label: 'WBSとは？作り方とガントチャートとの違い', path: '/guides/wbs/' },
        { label: 'WBSテンプレート', path: '/templates/wbs/' },
        { label: 'プロジェクト管理に生成AIを活用する方法', path: '/guides/project-management-ai/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20Japanese%20male%20office%20worker%20in%20his%2030s%20sitting%20at%20a%20clean%20modern%20desk%20in%20a%20bright%20sunlit%20open%20plan%20office%20uploading%20a%20printed%20project%20document%20to%20his%20laptop%20computer%20the%20laptop%20screen%20displays%20a%20colorful%20task%20list%20being%20automatically%20generated%20by%20AI%20with%20task%20names%20priority%20badges%20and%20deadline%20dates%20warm%20natural%20daylight%20streaming%20through%20large%20windows%20indoor%20plants%20wooden%20desk%20minimalist%20Japanese%20contemporary%20office%20interior%20refreshing%20clean%20atmosphere%20professional%20business%20casual%20attire&width=1200&height=750&seq=feature-ai-task-breakdown-01&orientation=landscape&nocache=true"
          alt="AIタスク分解の製品画面 - 資料をアップロードするとAIが自動でタスク一覧を生成"
          className="w-full h-auto"
        />
      </div>

      <div className="not-prose my-8 bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="p-6 lg:p-8">
            <h2 className="text-base font-bold text-foreground-900 mb-3">AIタスク分解のイメージ</h2>
            <p className="text-sm text-foreground-600 mb-4">左：アップロードした企画書（PDF）　→　右：AIが自動生成したタスク一覧</p>
            <div className="bg-background-50 rounded-md p-4 border border-background-200/70 mb-4">
              <p className="text-xs text-foreground-500 mb-2 font-medium">アップロード資料の例（企画書）</p>
              <div className="space-y-1.5 text-xs text-foreground-700">
                <p>■ プロジェクト名：顧客管理システム刷新</p>
                <p>■ 期間：2026年8月〜2027年1月</p>
                <p>■ 体制：PM 1名、開発 3名、デザイナー 1名</p>
                <p>■ 成果物：要件定義書、基本設計書、詳細設計書、</p>
                <p>　　テスト仕様書、運用マニュアル</p>
                <p>■ マイルストーン：</p>
                <p>　　8月末 要件定義完了 / 10月末 開発完了</p>
                <p>　　12月末 テスト完了 / 1月末 リリース</p>
              </div>
            </div>
          </div>
          <div className="p-6 lg:p-8 bg-primary-50 border-l border-background-200/70">
            <p className="text-xs text-primary-700 mb-2 font-medium">AIが自動生成したタスク一覧</p>
            <div className="space-y-2 text-xs">
              {[
                { task: '要件定義書作成', person: '山田', days: '10日', priority: '高', dep: '-' },
                { task: '基本設計書作成', person: '佐藤', days: '15日', priority: '高', dep: '要件定義書' },
                { task: '詳細設計書作成', person: '佐藤', days: '20日', priority: '中', dep: '基本設計書' },
                { task: 'データベース設計', person: '田中', days: '10日', priority: '高', dep: '基本設計書' },
                { task: '画面デザイン作成', person: '鈴木', days: '15日', priority: '中', dep: '要件定義書' },
                { task: 'フロントエンド開発', person: '田中', days: '30日', priority: '高', dep: '詳細設計書,画面デザイン' },
                { task: 'バックエンド開発', person: '佐藤', days: '30日', priority: '高', dep: '詳細設計書,DB設計' },
                { task: 'テスト仕様書作成', person: '山田', days: '10日', priority: '中', dep: '詳細設計書' },
                { task: '結合テスト実施', person: '全員', days: '15日', priority: '高', dep: '開発完了' },
                { task: '運用マニュアル作成', person: '山田', days: '10日', priority: '中', dep: 'テスト完了' },
              ].map((item) => (
                <div key={item.task} className="bg-background-50 rounded-md p-2 flex items-center justify-between text-foreground-700">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium truncate block">{item.task}</span>
                    <span className="text-foreground-500">担当: {item.person} / {item.days}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${item.priority === '高' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{item.priority}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-foreground-500 mt-3">※ AIの提案をユーザーが確認・修正・承認してからプロジェクトに反映します。</p>
          </div>
        </div>
      </div>

      <section>
        <h2>AIが提案する内容</h2>
        <p>AIが資料を読み取り、以下の項目を提案します。提案内容はユーザーが確認・修正・承認してから、ガントチャートへ反映します。</p>
        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
          {[
            { icon: 'ri-list-check', title: 'タスク名', desc: '必要な作業を具体的なタスクとして抽出' },
            { icon: 'ri-user-line', title: '担当者候補', desc: 'タスクに適したメンバーを提案' },
            { icon: 'ri-calendar-line', title: '開始日・期限・必要日数', desc: 'スケジュールを自動計算' },
            { icon: 'ri-flag-line', title: '優先度', desc: 'タスクの重要度と緊急度を判定' },
            { icon: 'ri-git-branch-line', title: '依存関係', desc: 'タスク間の前後関係を整理' },
            { icon: 'ri-star-line', title: 'マイルストーン', desc: '重要な節目を自動検出' },
            { icon: 'ri-file-copy-line', title: '成果物', desc: '各タスク完了時の成果物を特定' },
            { icon: 'ri-error-warning-line', title: '注意事項', desc: '資料から読み取ったリスクや留意点' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-primary-600 text-sm`}></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900">{item.title}</h3>
                <p className="text-xs text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>利用の流れ</h2>
        <div className="not-prose space-y-4 my-4">
          {[
            { step: '1', title: '資料をアップロード', desc: '企画書、提案書、WBS、議事録、仕様書、進捗表など、プロジェクトに関係する資料をアップロードします。複数ファイルの一括アップロードにも対応しています。' },
            { step: '2', title: 'AIがタスクを提案', desc: 'AIが資料の内容を読み取り、タスク一覧、担当者候補、スケジュール、依存関係を自動で提案します。処理は数分で完了します。' },
            { step: '3', title: '確認・修正・承認', desc: 'AIの提案内容をユーザーが確認し、必要に応じて修正した上で承認します。AIが自動的にタスクを確定することはありません。最終判断は常に人が行います。' },
            { step: '4', title: 'ガントチャートへ反映', desc: '承認されたタスクがガントチャートに自動反映され、依存関係やマイルストーンを含むプロジェクト計画が完成します。' },
          ].map((item) => (
            <div key={item.step} className="flex items-start gap-4 bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="w-9 h-9 bg-primary-500 text-background-50 rounded-full flex items-center justify-center text-sm font-bold shrink-0">{item.step}</div>
              <div>
                <h3 className="text-sm font-bold text-foreground-900">{item.title}</h3>
                <p className="text-xs text-foreground-600 mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>対応している資料形式</h2>
        <p>PDF、Excel、Word、PowerPoint、テキストファイルなど、幅広いファイル形式に対応しています。複数の資料をまとめてアップロードすることも可能です。ファイルサイズの制限は1ファイルあたり50MBまでです。</p>
      </section>

      <section>
        <h2>よくある質問</h2>
        <h3>AIの提案精度はどのくらいですか</h3>
        <p>資料の内容が明確で、プロジェクトの目的や範囲が具体的に記載されているほど、AIの提案精度は高くなります。最終的には人が確認・修正することを前提としているため、AIの提案は「たたき台」としてご活用ください。</p>
        <h3>アップロードした資料はAIの学習に使われますか</h3>
        <p>お客様がアップロードした資料がAIの追加学習に使用されることはありません。データはお客様のテナント内でのみ利用され、他社のAI処理に流用されることはありません。</p>
      </section>
    </PageShell>
  );
}